import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { hasPermission, isRestrictedVendor } from '../utils/permissions';
import { equipmentService, modalityProcedureMappingService } from '../services/dataService';
import EquipmentConnectionPanel from '../components/EquipmentConnectionPanel';
import WorklistQueriesTable from '../components/WorklistQueriesTable';
import DicomErrorsTable from '../components/DicomErrorsTable';
import {
  KpiCard,
  RawPayload,
  StatusBreakdown,
  StatusPill,
  toLabel,
} from '../components/DetailDashboard';
import styles from './assets/css/DetailDashboard.module.css';

const REQUEST_STATUS_ORDER = ['completed', 'in_progress', 'pending', 'failed', 'cancelled'];

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};

const EquipmentDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const canOpenFacilities = !isRestrictedVendor(user);
  const canPublish = hasPermission(user, 'equipment', 'write');
  const [equipment, setEquipment] = useState(null);
  const [summary, setSummary] = useState(null);
  const [connection, setConnection] = useState(null);
  const [queriesRefreshKey, setQueriesRefreshKey] = useState(0);
  const [modalityProcedures, setModalityProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [publishLoading, setPublishLoading] = useState(false);
  const [publishMessage, setPublishMessage] = useState('');
  const [publishError, setPublishError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadEquipment = async () => {
      setLoading(true);
      setError('');
      try {
        const [equipmentData, summaryData, connectionData] = await Promise.all([
          equipmentService.getById(id),
          equipmentService.getSummary(id),
          equipmentService.getConnection(id),
        ]);
        if (cancelled) return;
        setEquipment(equipmentData);
        setSummary(summaryData);
        setConnection(connectionData);

        if (equipmentData?.category) {
          modalityProcedureMappingService.getAll({ modality_code: equipmentData.category })
            .then((items) => { if (!cancelled) setModalityProcedures(items || []); })
            .catch(() => { if (!cancelled) setModalityProcedures([]); });
        }
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || err?.detail || 'Failed to load equipment details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (id) loadEquipment();
    return () => { cancelled = true; };
  }, [id]);

  const refreshConnection = useCallback(async () => {
    setQueriesRefreshKey((key) => key + 1);
    try {
      setConnection(await equipmentService.getConnection(id));
    } catch (_) {
      // Keep the last known connection state; the panel reports action errors itself.
    }
  }, [id]);

  const handlePublishToOrthanc = async () => {
    if (!equipment?.id) return;
    setPublishLoading(true);
    setPublishError('');
    setPublishMessage('');
    try {
      const result = await equipmentService.publishToOrthanc(equipment.id);
      setPublishMessage(
        `${result.message}: ${result.linked ? 'linked AE ' : ''}${result.dicom_aet}@${result.dicom_host}:${result.dicom_port}`,
      );
    } catch (err) {
      setPublishError(err?.response?.data?.detail || err?.message || 'Failed to publish equipment to Orthanc');
    } finally {
      setPublishLoading(false);
    }
  };

  const link = connection?.active_link;

  return (
    <div className={styles.page}>
      <Link className={styles.backLink} to="/equipment">← Back to Equipment</Link>

      {publishMessage ? <div className={styles.success}>{publishMessage}</div> : null}
      {publishError ? <div className={styles.error}>{publishError}</div> : null}

      {loading ? <div className={styles.card}>Loading equipment details...</div> : null}
      {!loading && error ? <div className={styles.error}>{error}</div> : null}

      {!loading && !error && equipment && summary && connection ? (
        <>
          <header className={styles.hero}>
            <div className={styles.heroMain}>
              <div className={styles.heroTitle}>
                <h1>{equipment.name}</h1>
                <StatusPill value={equipment.lifecycle_state} />
                <StatusPill value={equipment.operational_status} />
              </div>
              <div className={styles.codeRow}>
                {equipment.asset_id ? <span className={styles.code}>{equipment.asset_id}</span> : null}
                <span className={styles.code}>{equipment.category}</span>
                {equipment.dicom_aet ? <span className={styles.code}>AET {equipment.dicom_aet}</span> : null}
              </div>
              <div className={styles.heroMeta}>
                <span>
                  Vendor:{' '}
                  <Link to={`/vendors/${equipment.vendor_id}`}>{summary.vendor_name || equipment.vendor_id}</Link>
                </span>
                <span>
                  Facility:{' '}
                  {canOpenFacilities ? (
                    <Link to={`/facilities/${equipment.facility_id}`}>{summary.facility_name || equipment.facility_id}</Link>
                  ) : (
                    summary.facility_name || equipment.facility_id
                  )}
                </span>
              </div>
            </div>
            {canPublish ? (
              <div className={styles.heroSide}>
                <button
                  type="button"
                  className={styles.heroButton}
                  onClick={handlePublishToOrthanc}
                  disabled={publishLoading}
                >
                  {publishLoading ? 'Publishing...' : 'Republish to Orthanc'}
                </button>
              </div>
            ) : null}
          </header>

          <section className={styles.kpiGrid}>
            <KpiCard
              label="Requests"
              value={summary.total_requests}
              hint={`${summary.requests_last_30_days} in the last 30 days`}
            />
            <KpiCard
              label="Last Request"
              value={summary.last_request_at ? formatDate(summary.last_request_at) : '—'}
              hint={summary.last_request_at ? new Date(summary.last_request_at).toLocaleTimeString() : 'no requests yet'}
            />
            <KpiCard label="Active Worklists" value={summary.active_worklists} hint="published to Orthanc" />
            <KpiCard
              label="Online"
              value={toLabel(connection.online_status)}
              hint={link ? `${link.ae_title} @ ${link.ip_addr}` : 'not linked to a machine'}
            />
            <KpiCard
              label="Procedures"
              value={modalityProcedures.length}
              hint={`mapped to ${equipment.category}`}
            />
          </section>

          <section className={styles.chartGrid}>
            <div className={styles.card}>
              <h2>Requests by Status</h2>
              <StatusBreakdown
                counts={summary.request_status_counts}
                order={REQUEST_STATUS_ORDER}
                label="Request status distribution"
                emptyText="No requests have been routed to this machine yet."
              />
            </div>

            <div className={styles.card}>
              <h2>Configured DICOM Settings</h2>
              <dl className={styles.kvList}>
                <div><dt>System AE title</dt><dd><code>{equipment.dicom_aet || '—'}</code></dd></div>
                <div><dt>Fixed host</dt><dd>{equipment.dicom_host || '— (follows the machine’s IP)'}</dd></div>
                <div><dt>DICOM port</dt><dd>{equipment.dicom_port || '—'}</dd></div>
                <div><dt>Worklist ingestion</dt><dd>{toLabel(equipment.worklist_ingestion_method)}</dd></div>
                <div><dt>Transmission</dt><dd>{toLabel(equipment.transmission_method)}</dd></div>
              </dl>
              <p className={styles.cardNote}>
                Leave the fixed host empty for machines whose IP changes; the gateway then follows the linked machine&apos;s latest IP.
              </p>
            </div>
          </section>

          <EquipmentConnectionPanel
            equipmentId={equipment.id}
            connection={connection}
            canWrite={canPublish}
            onRefresh={refreshConnection}
          />

          <WorklistQueriesTable equipmentId={equipment.id} refreshKey={queriesRefreshKey} />

          <DicomErrorsTable equipmentId={equipment.id} refreshKey={queriesRefreshKey} canAcknowledge={canPublish} />

          <section className={styles.card}>
            <h2>Procedures via Modality ({equipment.category || '—'})</h2>
            {modalityProcedures.length ? (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>SHA Code</th>
                      <th>Procedure</th>
                      <th>Capable</th>
                      <th>Processing Time</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalityProcedures.map((mapping) => (
                      <tr key={mapping.id}>
                        <td><code>{mapping.sha_procedure_code}</code></td>
                        <td>{mapping.procedure_name || '—'}</td>
                        <td>
                          <StatusPill
                            value={mapping.is_capable ? 'active' : 'non_operational'}
                            label={mapping.is_capable ? 'Yes' : 'No'}
                          />
                        </td>
                        <td>{mapping.processing_time_minutes ? `${mapping.processing_time_minutes} min` : '—'}</td>
                        <td className={styles.muted}>{mapping.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className={styles.muted}>No procedures mapped to this modality yet.</p>
            )}
          </section>

          <section className={styles.profileGrid}>
            <div className={styles.card}>
              <h2>Equipment Profile</h2>
              <dl className={styles.fields}>
                <div><dt>Equipment ID</dt><dd><code>{equipment.id}</code></dd></div>
                <div><dt>Serial Number</dt><dd>{equipment.serial_number || '—'}</dd></div>
                <div><dt>Manufacturer</dt><dd>{equipment.manufacturer || '—'}</dd></div>
                <div><dt>Model</dt><dd>{[equipment.model, equipment.model_version].filter(Boolean).join(' ') || '—'}</dd></div>
                <div><dt>Software Version</dt><dd>{equipment.software_version || '—'}</dd></div>
                <div><dt>Country of Origin</dt><dd>{equipment.country_of_origin || '—'}</dd></div>
                <div><dt>Department</dt><dd>{equipment.department || '—'}</dd></div>
                <div><dt>Location</dt><dd>{equipment.location || '—'}</dd></div>
              </dl>
            </div>

            <div className={styles.card}>
              <h2>Workflow and Maintenance</h2>
              <dl className={styles.fields}>
                <div><dt>Worklist Ingestion</dt><dd>{toLabel(equipment.worklist_ingestion_method)}</dd></div>
                <div><dt>Transmission</dt><dd>{toLabel(equipment.transmission_method)}</dd></div>
                <div><dt>Last Maintenance</dt><dd>{formatDate(equipment.last_maintenance)}</dd></div>
                <div><dt>Next Maintenance</dt><dd>{formatDate(equipment.next_maintenance)}</dd></div>
                <div className={styles.fieldWide}>
                  <dt>Maintenance Notes</dt><dd>{equipment.maintenance_notes || '—'}</dd>
                </div>
                <div className={styles.fieldWide}>
                  <dt>Mandatory Worklist Fields</dt>
                  <dd>{equipment.mandatory_worklist_fields?.length ? equipment.mandatory_worklist_fields.join(', ') : '—'}</dd>
                </div>
              </dl>
            </div>
          </section>

          <RawPayload data={equipment} />
        </>
      ) : null}
    </div>
  );
};

export default EquipmentDetailPage;
